import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  SxProps,
  Theme,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import { useAuth } from '../../hooks/useAuth';
import { create } from 'lodash';

type NavItem = 'Home' | 'Features' | 'Pricing' | 'FAQ' | 'Support' | 'MCP Connect';

interface NavItemConfig {
  label: NavItem;
  href: string;
  target?: string;
}

const navItems: NavItemConfig[] = [
  { label: 'Home', href: 'https://txt2create.com' },
  { label: 'Features', href: 'https://txt2create.com/#features' },
  { label: 'Pricing', href: 'https://app.txt2create.com/express-checkout', target: '_blank' },
  { label: 'FAQ', href: 'https://txt2create.com/#faq' },
  { label: 'Support', href: 'https://support.txt2create.com/' },
  { label: 'MCP Connect', href: 'https://app.txt2create.com/connect-mcp' },
];

interface PublicNavProps {
  active?: NavItem;
  showCTAButtons?: boolean;
  mb?: number | string; // control bottom spacing per page
}

const t2cUrl = 'https://txt2create.com';

const getNavLinkStyle = (isActive: boolean): SxProps<Theme> => ({
  color: (theme) => theme.palette.common.white,
  fontSize: '14px',
  fontWeight: isActive ? 500 : 400,
  cursor: 'pointer',
  textDecoration: 'none',
  opacity: isActive ? 1 : 0.9,
  transition: 'opacity 0.2s ease',
  '&:hover': {
    opacity: 1,
  },
});

const PublicNav: React.FC<PublicNavProps> = ({ active, showCTAButtons = true, mb }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  // Check if user is logged in
  let isLoggedIn = false;
  try {
    const { auth } = useAuth();
    isLoggedIn = !!auth?.isAuthenticated;
  } catch {
    // useAuth may throw if not within AuthProvider, default to not logged in
    isLoggedIn = false;
  }

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleNavClick = (item: NavItemConfig) => {
    handleMenuClose();
    if (item.target === '_blank') {
      window.open(item.href, '_blank');
    } else {
      window.location.href = item.href;
    }
  };

  return (
    <Box
      sx={{
        ...styles.headerBar,
        mb: mb ?? (isXs ? 4 : 6),
        padding: (theme) => (isTablet || isMobile ? '16px 0px' : '16px 10%'),
      }}
    >
      <Box sx={styles.logoContainer} onClick={() => (window.location.href = t2cUrl)}>
        <NextImage
          src={'/assets/logoWithText.svg'}
          width={140}
          height={35}
          alt="TXT2create"
          style={{ borderRadius: 0 }}
        />
      </Box>

      {/* Desktop Navigation Links - Centered */}
      {!isMobile && (
        <Box sx={styles.navLinks}>
          {navItems.map((item) => (
            <Typography
              key={item.label}
              component="a"
              href={item.href}
              target={item.target}
              sx={getNavLinkStyle(active === item.label)}
            >
              {item.label}
            </Typography>
          ))}
        </Box>
      )}

      {/* CTA Buttons */}
      {showCTAButtons && (
        <Box sx={styles.ctaContainer}>
          {/* Login Button - Hidden if user is logged in */}
          {!isLoggedIn ? (
            <>
              {isMobile ? (
                <IconButton
                  sx={styles.loginIconBtn}
                  onClick={() => (window.location.href = 'https://app.txt2create.com')}
                  aria-label="Login or Sign up"
                >
                  <NextImage
                    src="/assets/login_button.png"
                    width={40}
                    height={40}
                    alt="Login"
                    style={{ borderRadius: 0 }}
                  />
                </IconButton>
              ) : (
                <Button
                  sx={styles.loginBtn}
                  variant="outlined"
                  onClick={() => (window.location.href = 'https://app.txt2create.com')}
                >
                  Login / Sign Up
                </Button>
              )}
            </>
          ) : (
            <Button
              sx={styles.createBtn}
              onClick={() => (window.location.href = 'https://app.txt2create.com/dashboard')}
            >
              Create
            </Button>
          )}

          {/* Hamburger Menu - Mobile/Tablet only */}
          {isMobile && (
            <>
              <IconButton
                sx={styles.menuIconBtn}
                onClick={handleMenuOpen}
                aria-label="Toggle menu"
                aria-controls={isMenuOpen ? 'nav-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
              >
                <NextImage
                  src="/assets/menu.png"
                  width={30}
                  height={30}
                  alt="Menu"
                  style={{ borderRadius: 0 }}
                />
              </IconButton>
              <Menu
                id="nav-menu"
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'menu-button',
                  sx: { py: 0.5 },
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                sx={styles.menuDropdown}
              >
                {navItems.map((item, index) => (
                  <React.Fragment key={item.label}>
                    <MenuItem onClick={() => handleNavClick(item)} sx={styles.menuItem}>
                      <Typography
                        sx={{
                          color: 'common.white',
                          fontSize: '13px',
                          fontWeight: active === item.label ? 500 : 400,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        {item.label}
                      </Typography>
                    </MenuItem>
                    {index < navItems.length - 1 && <Divider sx={styles.menuDivider} />}
                  </React.Fragment>
                ))}
              </Menu>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

const styles: Styles = {
  headerBar: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoContainer: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  navLinks: {
    display: 'flex',
    gap: { xs: 2, sm: 3, md: 4 },
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  ctaContainer: {
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    flex: '0 0 auto',
  },
  loginBtn: {
    borderColor: 'rgba(255, 255, 255, 0.2)',
    color: (theme) => theme.palette.common.white,
    borderRadius: '8px',
    px: 2.5,
    py: 0.75,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.4)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  createBtn: {
    backgroundColor: (theme) => theme.palette.primary.main,
    color: (theme) => theme.palette.common.white,
    borderRadius: '50px',
    px: 2.5,
    py: 0.75,
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: (theme) => theme.palette.primary.dark,
    },
  },
  loginIconBtn: {
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    width: 40,
    height: 40,
    p: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.4)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  menuIconBtn: {
    borderRadius: '8px',
    width: 40,
    height: 40,
    p: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.05)',
    },
  },
  menuDropdown: {
    '& .MuiPaper-root': {
      background: 'rgba(6, 10, 18, 0.9)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '12px',
      mt: 1.5,
      minWidth: 'fit-content',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
      overflow: 'hidden',
    },
    '& .MuiList-root': {
      py: 0,
    },
  },
  menuItem: {
    py: 1.25,
    px: 2,
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  menuDivider: {
    borderColor: 'rgba(255, 255, 255, 0.08)',
    mx: 1.5,
    my: 0,
  },
};

export default PublicNav;
